<template>
  <div id="connectivityCore">
    <avatar-loader v-if="props.showVideo" connectivity="true" />
    <section v-show="props.showVideo" class="videoPreview" :class="[hasSpinner ? 'loading' : 'loaded']">
      <div id="connectivityVideos" class="videos">
        <div class="userPic">
          <img v-if="user && avatar" id="connectivityImg" :src="avatar" />
          <app-scaled-text v-if="!avatar">{{ initials }}</app-scaled-text>
        </div>
        <div class="connectivityVideo" :class="{ stacked: isVideoDevicesAvailable }">
          <video id="connectivityVideo" :controls="false" @canplaythrough="onConnectivityVideoLoaded" />
        </div>
        <audio id="connectivityAudio" class="connectivityAudio" />
      </div>
    </section>

    <div class="deviceSelect">
      <connectivity-settings-device
        type="video"
        device="camera"
        :currentId="videoSource"
        :on="videoOn"
        @change="onVideoDeviceChanged"
        direction="in"
        :canTurnOff="true"
      />
      <connectivity-settings-device
        type="audio"
        device="microphone"
        :currentId="audioSource"
        :on="isAudioInputEnabled"
        :canTurnOff="false"
        @change="onAudioDeviceChanged"
        direction="in"
      />
      <connectivity-settings-device
        v-if="!isFirefox"
        type="audio"
        device="speaker"
        :currentId="audioOutputSource"
        :on="isAudioOutputEnabled"
        :canTurnOff="false"
        @change="onAudioOutputDeviceChanged"
        direction="out"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '@/store/pinia/account';
import { useMediaStore } from '@/store/pinia/media';
import { usePageStore } from '@/store/pinia/page';
import { useUsersStore } from '@/store/pinia/users';

import AppScaledText from '@/components/AppScaledText.vue';
import ConnectivitySettingsDevice from '@/components/ConnectivitySettingsDevice.vue';
import AvatarLoader from '@/components/AvatarLoader.vue';
import utils from '../resources/utils';

const props = defineProps({
  showVideo: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['audio:change', 'audio-output:change', 'video:change']);
const accountStore = useAccountStore();
const pageStore = usePageStore();
const mediaStore = useMediaStore();
const usersStore = useUsersStore();
const { audioSource, audioOutputSource, videoSource, audioOn, audioOutputOn, videoOn } = storeToRefs(mediaStore);
const { currentPageName, isMeeting } = storeToRefs(pageStore);
const { currentUser: user } = storeToRefs(accountStore);

const isChanged = ref({
  audioDevice: false,
  audioOutputDevice: false,
});
const hasSpinner = computed(() => usersStore.userHasSpinner('connectivity'));
const isFirefox = computed(() => navigator.userAgent.indexOf('Firefox') !== -1);
const isConnectivityCheckPage = computed(() => currentPageName.value === 'connectivity');
const avatar = computed(() => user.value?.picture_url);
const initials = computed(() => {
  if (user.value) {
    return utils.desanitizeString(user.value.first_name[0].toUpperCase()) + utils.desanitizeString(user.value.last_name[0].toUpperCase());
  }
  return 'ME';
});
const isVideoDevicesAvailable = computed(() => videoOn.value && videoSource.value?.length > 0);
const isAudioInputEnabled = computed(() => (isConnectivityCheckPage.value && !isChanged.value.audioDevice ? true : audioOn.value));
const isAudioOutputEnabled = computed(() => (isConnectivityCheckPage.value && !isChanged.value.audioOutputDevice ? true : audioOutputOn.value));

function onAudioDeviceChanged(deviceId) {
  isChanged.value.audioDevice = true;
  const ignoreUpdatingMuteState = deviceId && isMeeting.value; // user's just changing devices, not unmuting themselves.
  if (!ignoreUpdatingMuteState) {
    mediaStore.setAudioOn(!!deviceId);
  }
  emit('audio:change', deviceId);
}

function onAudioOutputDeviceChanged(deviceId) {
  isChanged.value.audioOutputDevice = true;
  mediaStore.setAudioOutputOn(!!deviceId);
  emit('audio-output:change', deviceId);
}

function onConnectivityVideoLoaded() {
  usersStore.setVideoLoaded('connectivity', true, 'connectivity canplaythrough');
}

function onVideoDeviceChanged(deviceId) {
  const ignoreUpdatingMuteState = deviceId && isMeeting.value; // user's just changing devices, not unmuting themselves.
  if (!ignoreUpdatingMuteState) {
    mediaStore.setVideoOn(!!deviceId);
  }
  emit('video:change', deviceId);
}
</script>
<style lang="scss" scoped>
.userPic img {
  width: 100%;
  object-fit: cover;
}

section.videoPreview {
  height: rem(300px);
  background: var(--c__bg);
  border-width: 2px;
  border-color: var(--c__avatar-border-edge);
  border-style: var(--border-style__avatar);
  border-radius: 50% !important;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  position: relative;
  margin-bottom: 2em;
  transition: all 250ms ease-in-out;

  &.loading {
    opacity: 0;
    transform: scale(0);
  }
}

.loadingAvatar {
  height: rem(262px);
  width: rem(262px);
  z-index: 10;
}

.videos {
  position: absolute;
  top: 2.8%;
  left: 2.8%;
  bottom: 2.8%;
  right: 2.8%;
  border-radius: 50% !important;
  overflow: hidden;
  background: var(--grad__initials);

  :deep(text) {
    fill: var(--c__bg);
  }

  .connectivityVideo {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    border-radius: 50%;
    overflow: hidden;
    pointer-events: none;

    &.stacked {
      z-index: 2;
    }

    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: black;
      border-radius: 50%;
      animation: fadeOut 300ms 400ms forwards;
    }

    video {
      transform: rotateY(180deg);
      height: 100%;
      width: 100%;
      -o-object-fit: cover;
      object-fit: cover;
      position: absolute;
    }
  }
}
</style>
